import {
  Collapse,
  IconButton,
  Paper,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
} from "@mui/material";
import clsx from "clsx";
import React from "react";
import { t } from "ttag";
import { Delete } from "../../icons";
import { ExpandMore, ContentCopy } from "@mui/icons-material";
import MockDeviceHierarchyPanel from "./MockDeviceHierarchyPanel";
import MockDeviceStateSwitches from "./MockDeviceStateSwitches";
import useStyles from "./VirtualDevices.style";

const HIERARCHY_ROW_COL_SPAN = 9;

const VirtualDevicesTable = ({
  devices,
  expandedDeviceId,
  onToggleExpand,
  onRowDoubleClick,
  onRequestDelete,
  onRequestUnregister,
  onCopyValue,
  concurrentSimulatorConnections,
  maxConcurrentSimulatorConnections,
  ariaLabel,
}: any) => {
  const styles: any = useStyles();

  const handleRowDoubleClick = (id: string) => () => {
    onRowDoubleClick(id);
  };

  const handleToggleExpandClick = (id: string) => () => {
    onToggleExpand(id);
  };

  const handleCopyClick = (value: string) => () => {
    onCopyValue(value);
  };

  const handleDeleteClick = (id: string) => () => {
    onRequestDelete(id);
  };

  return (
    <Paper elevation={0} className={styles.paperPadding}>
      <TableContainer>
        <Table stickyHeader aria-label={ariaLabel}>
          <TableHead className={styles.tableHead}>
            <TableRow>
              <TableCell className={clsx(styles.headCells, styles.expandCell)} />
              <TableCell className={styles.headCells}>{t`NAME`}</TableCell>
              <TableCell className={styles.headCells}>{t`SERIAL`}</TableCell>
              <TableCell className={styles.headCells}>{t`PIN`}</TableCell>
              <TableCell align="center" className={styles.headCells}>{t`CONNECTED`}</TableCell>
              <TableCell align="center" className={styles.headCells}>{t`REGISTERED`}</TableCell>
              <TableCell align="center" className={styles.headCells}>{t`BLOCKED`}</TableCell>
              <TableCell align="center" className={styles.headCells}>{t`PRO`}</TableCell>
              <TableCell align="center" className={styles.headCells}>{t`DELETE`}</TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {devices.map((device: any) => {
              const isExpanded = expandedDeviceId === device.id;

              return (
                <React.Fragment key={device.id}>
                  <TableRow hover tabIndex={-1} onDoubleClick={handleRowDoubleClick(device.id)}>
                    <TableCell className={styles.expandCell}>
                      <IconButton size="small" onClick={handleToggleExpandClick(device.id)}>
                        <ExpandMore className={isExpanded ? styles.expandIconOpen : ""} />
                      </IconButton>
                    </TableCell>
                    <TableCell className={styles.rowCell}>{device.name}</TableCell>
                    <TableCell className={styles.rowCell}>
                      <div className={styles.copyableCell}>
                        {device.serial}
                        <IconButton size="small" onClick={handleCopyClick(device.serial)}>
                          <ContentCopy fontSize="small" />
                        </IconButton>
                      </div>
                    </TableCell>
                    <TableCell className={styles.rowCell}>
                      <div className={styles.copyableCell}>
                        {device.pin}
                        <IconButton size="small" onClick={handleCopyClick(device.pin)}>
                          <ContentCopy fontSize="small" />
                        </IconButton>
                      </div>
                    </TableCell>
                    <TableCell align="center" className={styles.rowCell}>
                      <MockDeviceStateSwitches
                        device={device}
                        field="connected"
                        disabled={
                          device.simulatorConnected
                          && !device.connected
                          && concurrentSimulatorConnections >= maxConcurrentSimulatorConnections
                        }
                        disabledReason={t`Limit of ${maxConcurrentSimulatorConnections} concurrent simulator-connected devices reached`}
                      />
                    </TableCell>
                    <TableCell align="center" className={styles.rowCell}>
                      <MockDeviceStateSwitches
                        device={device}
                        field="registered"
                        onRequestUnregisterConfirm={onRequestUnregister}
                      />
                    </TableCell>
                    <TableCell align="center" className={styles.rowCell}>
                      <MockDeviceStateSwitches device={device} field="blocked" />
                    </TableCell>
                    <TableCell align="center" className={styles.rowCell}>
                      <MockDeviceStateSwitches device={device} field="proLicense" />
                    </TableCell>
                    <TableCell align="center" className={styles.rowCell}>
                      <IconButton onClick={handleDeleteClick(device.id)}>
                        <Delete />
                      </IconButton>
                    </TableCell>
                  </TableRow>
                  <TableRow>
                    <TableCell className={styles.hierarchyCell} colSpan={HIERARCHY_ROW_COL_SPAN}>
                      <Collapse in={isExpanded} unmountOnExit>
                        {isExpanded && <MockDeviceHierarchyPanel deviceId={device.id} />}
                      </Collapse>
                    </TableCell>
                  </TableRow>
                </React.Fragment>
              );
            })}
          </TableBody>
        </Table>
      </TableContainer>
    </Paper>
  );
};

export default VirtualDevicesTable;
